import {
  ReverseProxyController,
  ReverseProxyEventMap,
  ReverseProxyURLOptions,
} from "@src/components/ReverseProxy/ReverseProxyController";
import {
  ProxyErrorReport,
  ProxyState,
} from "@intelligems/ig-embedded-preview/messages";

export type ProxyLoadState = {
  type: "ready" | "error";
} & Record<string, unknown>;

export type ProxyStateEventMap = ReverseProxyEventMap & {
  loadState: ProxyLoadState | undefined;
  location: string | undefined;
};

export type ProxyStateURLOptions = ReverseProxyURLOptions;

export class ProxyStateController<
  Events extends ProxyStateEventMap = ProxyStateEventMap,
  URLOptions extends ProxyStateURLOptions = ProxyStateURLOptions,
> extends ReverseProxyController<Events, URLOptions> {
  protected loadState: ProxyLoadState | undefined;
  protected location: string | undefined;
  protected timeoutDuration: number;

  protected timeoutId: NodeJS.Timeout;
  protected timerFired: boolean;
  protected errorFired: boolean;
  protected commitPending = false;
  protected proxyState: ProxyState | undefined;
  protected proxyErrorReport: ProxyErrorReport | undefined;

  constructor() {
    super();
    this.timeoutDuration = 3_000;
  }

  protected update() {
    const loadState = this.getLoadState();
    if (JSON.stringify(loadState) !== JSON.stringify(this.loadState)) {
      this.loadState = loadState;
      this.emit("loadState", loadState);
    }
    const location = this.getLocation();
    if (location !== this.location) {
      this.location = location;
      this.emit("location", location);
    }
  }

  override onProxyState(proxyState: ProxyState) {
    super.onProxyState(proxyState);
    this.errorFired = false;
    this.proxyState = proxyState;
    this.proxyErrorReport = undefined;
    // A fresh proxy load means the navigation has committed to its new page.
    // Don't rely solely on `ig:pagehide` (via `onNavigateCommit`) to clear
    // `commitPending`: on a cross-origin navigation that message arrives
    // sourceless from the *old* proxy origin, which no longer matches the
    // swapped-in target origin, so it's dropped by the message guard. That left
    // `commitPending` stuck true and made `getLoadState` short-circuit to
    // "ready" after the timer fired — masking a missing-script/proxy error
    // (e.g. navigating to a subdomain without the plugin) as a healthy page.
    this.commitPending = false;
    this.update();
  }

  override onInitialized() {
    super.onInitialized();
    this.startPageLoad();
  }

  override onNavigate() {
    super.onNavigate();
    this.startPageLoad();
    this.commitPending = true;
  }

  override onNavigateCommit() {
    super.onNavigateCommit();
    this.commitPending = false;
  }

  override onProxyError(report: ProxyErrorReport) {
    this.proxyErrorReport = report;
    super.onProxyError(report);
  }

  override onError() {
    super.onError();
    this.proxyState = undefined;
    this.errorFired = true;

    this.update();
  }

  protected startPageLoad() {
    this.resetState();

    clearTimeout(this.timeoutId);
    this.timeoutId = setTimeout(() => this.onTimeout(), this.timeoutDuration);

    this.update();
  }

  protected onTimeout() {
    this.timerFired = true;
    this.update();
  }

  protected getLoadState(): ProxyLoadState | undefined {
    if (this.errorFired || this.targetUrl === null) {
      return { type: "error" };
    }
    if (this.proxyState || this.isAuthPageLoaded) {
      return { type: "ready" };
    }
    if (this.timerFired) {
      if (this.commitPending) {
        return { type: "ready" };
      }
      if (!this.proxyState) {
        return { type: "error", subtype: "proxy" };
      }
    }
    return undefined;
  }

  protected getLocation() {
    return this.proxyState?.location;
  }

  protected resetState() {
    this.timerFired = false;
    this.errorFired = false;
    this.proxyState = undefined;
    this.commitPending = false;
    this.proxyErrorReport = undefined;
  }

  protected get isAuthPageLoaded() {
    return this.proxyState?.location?.endsWith("/password");
  }
}
